import {
  GeoSpatialFileType,
  getAllPrescriptionsApi,
  locutusCreateRxApi,
  type PlanAssetCreateErrorResult
} from 'api-validations/locutus'
import type { ControllableApplication } from 'api-validations/stitch'
import { Feature } from 'ol'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useApiClient, useMutation, useQueryClient } from 'shared-ui/api-provider'
import type { MeasureSystem } from 'shared-ui/localization'
import { useMapContext } from 'shared-ui/map'
import { Geo, isNil } from 'utils'
import type z from 'zod'

type CreateRxResponse = z.infer<typeof locutusCreateRxApi.responseSchema>

type PrescriptionAssets = {
  applicationType: ControllableApplication
  measureSystem: MeasureSystem
  fieldId: string
  name: string
  attributes: { name: string; zones: { [id: string]: number } }[]
}

/**
 * Mutation for creating a GeoJSON-based boundary asset
 */
export type PrescriptionSave =
  | { saved: true; prescription: CreateRxResponse['items'][number] }
  | { saved: false; name: string; error: PlanAssetCreateErrorResult }

export const useCreatePrescription = (onSettled: (saves: PrescriptionSave[]) => void) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { map } = useMapContext()

  return useMutation({
    mutationFn: async (assets: PrescriptionAssets[]): Promise<PrescriptionSave[]> => {
      const features = map.drawTool.getFeatures()
      return Promise.all(
        assets.map(async (asset) => {
          const feats = Geo.GeoJSON.fromOpenlayersFeatures(
            features.flatMap((feat) => {
              if (isNil(feat.getId())) return []
              const feature = new Feature(feat.getGeometry())
              // feat.getProperties() returns `any` from OpenLayers — zone is always a string key
              const zone = feat.getProperties().zone as string
              const values = Object.fromEntries(
                asset.attributes.map(({ name, zones }) => [name, zones[zone] ?? 0])
              )
              feature.setProperties(values ?? {})
              return feature
            }),
            (g) => g.type === 'Polygon' || g.type === 'MultiPolygon'
          )
          return apiClient
            .callApi(
              locutusCreateRxApi,
              { orgCode },
              {
                ...asset,
                type: GeoSpatialFileType.GeoJSON,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any -- TODO: @aubrey -- this is awkward because the types we have at this point are just generic geojson and we need the rx type specifically. It will be parsed in the next step, so it's fine, but it's awkward type wise
                prescription: feats as any
              }
            )
            .then(
              ({ items: [prescription] }): PrescriptionSave => ({ saved: true, prescription }),
              // callApi runs the API's errorMap, so a 409 surfaces as a typed
              // PlanAssetCreateError the caller can localize — no manual parsing needed.
              (error: PlanAssetCreateErrorResult): PrescriptionSave => ({
                saved: false,
                name: asset.name,
                error
              })
            )
        })
      )
    },
    onSuccess: (saves) => {
      onSettled(saves)
      if (saves.some(({ saved }) => saved)) {
        queryClient.invalidateQueries({
          queryKey: getAllPrescriptionsApi.queryKey({ orgCode })
        })
      }
    }
  })
}
